import { Injectable, computed, signal } from "@angular/core";
import { TagCategoryKey } from "../../../../models/freeform-tags.model";
import { SubmissionFreeformSearchRequest } from "../../../models/submission-freeform.model";

export const COLUMN_OPTIONS = [
    "Score",
    "Queue",
    "Status",
    "Interactive",
    "Date",
    "Name",
    "Location",
    "Summary",
    "Type",
    "Segment",
    "Source"
] as const;

export interface TagSelection {
    categoryKey: TagCategoryKey;
    tag: string;
}

export const MIN_SCORE_OPTIONS = [90, 80, 70, 60, 50, 40, 30, 20, 10] as const;
export type MinScoreValue = (typeof MIN_SCORE_OPTIONS)[number];

export const FILTER_BY_OPTIONS = [
    { value: "isFavorite", label: "Favorited" },
    { value: "isUsed", label: "Used" },
    { value: "hasResponse", label: "Response" },
    { value: "isVoice", label: "Voice" },
    { value: "isText", label: "Text" },
    { value: "isVideo", label: "Video" },
    { value: "hasPhoneNumber", label: "Phone # Included" }
] as const satisfies readonly { value: keyof SubmissionFreeformSearchRequest; label: string }[];
export type FilterByValue = (typeof FILTER_BY_OPTIONS)[number]["value"];

export const PUBLIC_DISPLAY_OPTIONS = [
    { value: "off", label: "Off" },
    { value: "allowed", label: "Public Display Allowed" },
    { value: "notAllowed", label: "Public Display Not Allowed" }
] as const;
export type PublicDisplayValue = (typeof PUBLIC_DISPLAY_OPTIONS)[number]["value"];

export const VISIBILITY_OPTIONS = [
    { value: "showSpam", label: "Show Spam" },
    { value: "hideArchived", label: "Hide Archived" },
    { value: "hideUsed", label: "Hide Used" }
] as const;
export type VisibilityValue = (typeof VISIBILITY_OPTIONS)[number]["value"];

interface SubmittedByFilter {
    field: "profileId" | "deviceId";
    value: string;
}

export interface FilterLabel {
    value: string;
    label?: string;
}

/**
 * Holds the filters that scope the freeform submissions view (producer dashboard).
 *
 * Not provided in root because the service is scoped to the producer dashboard so
 * filters reset when the user navigates away.
 */
@Injectable()
export class FreeformFilterService {
    private readonly _availableColumns = signal<readonly string[]>(COLUMN_OPTIONS);
    readonly availableColumns = this._availableColumns.asReadonly();

    private readonly _selectedColumns = signal<readonly string[]>(COLUMN_OPTIONS);
    readonly selectedColumns = this._selectedColumns.asReadonly();

    private readonly _tags = signal<readonly TagSelection[]>([]);
    readonly tags = this._tags.asReadonly();

    private readonly _searchQuery = signal("");
    readonly searchQuery = this._searchQuery.asReadonly();

    private readonly _submittedBy = signal<SubmittedByFilter | null>(null);
    readonly submittedBy = computed(() => this._submittedBy()?.value ?? "");

    private readonly _minScore = signal<number | null>(null);
    readonly minScore = this._minScore.asReadonly();

    private readonly _segmentId = signal<string | null>(null);
    readonly segmentId = this._segmentId.asReadonly();

    private readonly _filterBy = signal<readonly FilterByValue[]>([]);
    readonly filterBy = this._filterBy.asReadonly();

    private readonly _publicDisplay = signal<PublicDisplayValue>("off");
    readonly publicDisplay = this._publicDisplay.asReadonly();

    private readonly _visibility = signal<readonly VisibilityValue[]>([]);
    readonly visibility = this._visibility.asReadonly();

    readonly isUsedFilterEnabled = computed(() => !this._visibility().includes("hideUsed"));

    private flag(enabled: boolean): true | undefined {
        return enabled ? true : undefined;
    }

    /** Consolidates all the filter fields for a submissions search request. */
    readonly searchFilter = computed(
        (): Pick<
            SubmissionFreeformSearchRequest,
            | "keyword"
            | "minimumScore"
            | "isFavorite"
            | "isUsed"
            | "isPublic"
            | "isVoice"
            | "isText"
            | "isVideo"
            | "hasResponse"
            | "hasPhoneNumber"
            | "profileId"
            | "deviceId"
            | "isSpam"
            | "state"
            | "tags"
            | "segmentId"
        > => {
            const filterBy = this._filterBy();
            const visibility = this._visibility();
            const submittedBy = this._submittedBy();
            const publicDisplay = this._publicDisplay();

            const tagsByCategory = this._tags().reduce<Partial<Record<TagCategoryKey, string[]>>>(
                (acc, { categoryKey, tag }) => {
                    acc[categoryKey] = [...(acc[categoryKey] ?? []), tag];
                    return acc;
                },
                {}
            );

            return {
                keyword: this._searchQuery() || undefined,
                minimumScore: this._minScore() ?? undefined,
                segmentId: this._segmentId() ?? undefined,
                isFavorite: this.flag(filterBy.includes("isFavorite")),
                isVoice: this.flag(filterBy.includes("isVoice")),
                isText: this.flag(filterBy.includes("isText")),
                isVideo: this.flag(filterBy.includes("isVideo")),
                hasResponse: this.flag(filterBy.includes("hasResponse")),
                hasPhoneNumber: this.flag(filterBy.includes("hasPhoneNumber")),
                profileId: submittedBy?.field === "profileId" ? submittedBy.value : undefined,
                deviceId: submittedBy?.field === "deviceId" ? submittedBy.value : undefined,
                isUsed: visibility.includes("hideUsed") ? false : this.flag(filterBy.includes("isUsed")),
                isPublic: publicDisplay === "off" ? undefined : publicDisplay === "allowed",
                isSpam: visibility.includes("showSpam") ? undefined : false,
                state: visibility.includes("hideArchived") ? "ACTIVE" : undefined,
                tags: Object.keys(tagsByCategory).length > 0 ? tagsByCategory : undefined
            };
        }
    );

    /** Every active filter as a display line for the active-filters overlay, tags included — in a fixed order:
     *  tags, filterBy, minScore, publicDisplay, columns, submittedBy (visibility toggles are intentionally
     *  not counted as filters from design request). */
    filterLabels(
        columnLabel: (categoryKey: TagCategoryKey) => string,
        segmentLabel: (segmentId: string) => string
    ): FilterLabel[] {
        const tagLabels: FilterLabel[] = this._tags().map(({ categoryKey, tag }) => ({
            label: columnLabel(categoryKey),
            value: tag
        }));

        const filterByLabels: FilterLabel[] = this._filterBy().map(value => ({
            value: FILTER_BY_OPTIONS.find(o => o.value === value)!.label
        }));

        const minScoreValue = this._minScore();
        const minScoreLabel: FilterLabel[] = minScoreValue === null ? [] : [{ value: `${minScoreValue}+` }];

        const segmentIdValue = this._segmentId();
        const segmentIdLabel: FilterLabel[] = segmentIdValue === null ? [] : [{ value: segmentLabel(segmentIdValue) }];

        const publicDisplayValue = this._publicDisplay();
        const publicDisplayLabel: FilterLabel[] =
            publicDisplayValue === "off"
                ? []
                : [{ value: PUBLIC_DISPLAY_OPTIONS.find(o => o.value === publicDisplayValue)!.label }];

        const hiddenCount = this._availableColumns().length - this._selectedColumns().length;
        const columnsLabel: FilterLabel[] = hiddenCount > 0 ? [{ value: `Columns: ${hiddenCount} Hidden` }] : [];

        const submittedByLabel: FilterLabel[] = this.submittedBy() === "" ? [] : [{ value: "View All From Listener" }];

        return [
            ...tagLabels,
            ...filterByLabels,
            ...minScoreLabel,
            ...segmentIdLabel,
            ...publicDisplayLabel,
            ...columnsLabel,
            ...submittedByLabel
        ];
    }

    readonly activeFilterCount = computed(
        () =>
            this._tags().length +
            this._filterBy().length +
            (this._minScore() === null ? 0 : 1) +
            (this._segmentId() === null ? 0 : 1) +
            (this._publicDisplay() === "off" ? 0 : 1) +
            (this._availableColumns().length - this._selectedColumns().length > 0 ? 1 : 0) +
            (this.submittedBy() === "" ? 0 : 1)
    );

    clearAll(): void {
        this._tags.set([]);
        this._filterBy.set([]);
        this._minScore.set(null);
        this._segmentId.set(null);
        this._publicDisplay.set("off");
        this._selectedColumns.set(this._availableColumns());
        this._visibility.set([]);
        this._submittedBy.set(null);
    }

    isTagSelected(categoryKey: TagCategoryKey, tag: string): boolean {
        return this._tags().some(t => t.categoryKey === categoryKey && t.tag === tag);
    }

    toggleTag(categoryKey: TagCategoryKey, tag: string): void {
        const current = this._tags();
        const idx = current.findIndex(t => t.categoryKey === categoryKey && t.tag === tag);
        this._tags.set(idx === -1 ? [...current, { categoryKey, tag }] : current.filter((_, i) => i !== idx));
    }

    setTags(tags: readonly TagSelection[]): void {
        this._tags.set(tags);
    }

    setSearchQuery(query: string): void {
        this._searchQuery.set(query);
    }

    setSubmittedBy(profileId: string | null, deviceId: string | null): void {
        if (profileId) {
            this._submittedBy.set({ field: "profileId", value: profileId });
        } else if (deviceId) {
            this._submittedBy.set({ field: "deviceId", value: deviceId });
        } else {
            this._submittedBy.set(null);
        }
    }

    setFilterBy(values: readonly FilterByValue[]): void {
        this._filterBy.set(values);
    }

    toggleFilterBy(value: FilterByValue): void {
        const current = this._filterBy();
        this.setFilterBy(current.includes(value) ? current.filter(v => v !== value) : [...current, value]);
    }

    setMinScore(value: number | null): void {
        this._minScore.set(value);
    }

    setSegmentId(value: string | null): void {
        this._segmentId.set(value);
    }

    setPublicDisplay(value: PublicDisplayValue): void {
        this._publicDisplay.set(value);
    }

    setAvailableColumns(options: readonly string[]): void {
        this._availableColumns.set(options);
        this._selectedColumns.set(options);
    }

    setSelectedColumns(values: readonly string[]): void {
        this._selectedColumns.set(values);
    }

    setVisibility(values: readonly VisibilityValue[]): void {
        this._visibility.set(values);
        if (values.includes("hideUsed") && this._filterBy().includes("isUsed")) {
            // "Used" option in the "Filter By" filter is disabled while "Hide Used" visibility is enabled
            this._filterBy.set(this._filterBy().filter(v => v !== "isUsed"));
        }
    }

    toggleVisibility(value: VisibilityValue): void {
        const current = this._visibility();
        this.setVisibility(current.includes(value) ? current.filter(v => v !== value) : [...current, value]);
    }
}
